import React from "react";
import Datatable from "./common/datatable";
import { useNavigate } from "react-router-dom";
import { useViewIndustryQuery } from "../hooks/industry/industryList";
import { useViewBrandQuery } from "../hooks/brand/brand.all.js";
import ActionMenu from "./common/menu";
import {
  getApplicationImageStatus,
  getApplicationImageStatusClass,
} from "../utils/applicationImageStatus";
import FallbackImage from "./common/fallbackImage";

const ApplicationImagesData = ({
  data = [],
  onEdit,
  onView,
  onPending,
  onApprove,
  canApprove = true,
}) => {
  const { data: industriesData } = useViewIndustryQuery();
  const { data: brandsData } = useViewBrandQuery();
  const navigate = useNavigate();

  const industriesList = industriesData?.data || industriesData || [];
  const brandsList = brandsData?.data || brandsData || [];

  const handleView = (applicationImages) => {
    const id = applicationImages._id || applicationImages.id;
    if (onView) {
      onView(applicationImages);
    } else if (id) {
      navigate(`/applicationImages/view/${id}`);
    }
  };
  const handleEdit = (applicationImages) => {
    if (onEdit) {
      onEdit(applicationImages);
    }
  };

  

  const getFirstImagePath = (row) => {
    const gallery = row?.gallery || row?.images || row?.media?.images;
    if (Array.isArray(gallery) && gallery.length > 0) {
      const first = gallery[0];
      if (typeof first === "string") return first;
      return first?.url || first?.path || first?.image || null;
    }
    return (
      row?.image ||
      row?.imageUrl ||
      row?.url ||
      row?.logo ||
      row?.filePath ||
      null
    );
  };

  const getImageSrc = (row) => getFirstImagePath(row);

  const getIndustryTitle = (val) => {
    if (!val || (Array.isArray(val) && val.length === 0)) return "-";

    if (Array.isArray(val)) {
      return val
        .map((item) => {
          if (typeof item === "string") {
            const found = industriesList.find(
              (i) => i._id === item || i.id === item,
            );
            return found ? found.title : item;
          }
          if (item?.title) return item.title;
          if (item?._id) {
            const found = industriesList.find(
              (i) => i._id === item._id || i.id === item._id,
            );
            return found ? found.title : item._id;
          }
          return "-";
        })
        .join(", ");
    }

    if (typeof val === "string") {
      const found = industriesList.find((i) => i._id === val || i.id === val);
      return found ? found.title : val;
    }

    if (typeof val === "object") {
      if (val.title) return val.title;
      if (val._id) {
        const found = industriesList.find(
          (i) => i._id === val._id || i.id === val._id,
        );
        return found ? found.title : val._id;
      }
    }

    return "-";
  };

  const getBrandTitle = (val) => {
    if (!val || (Array.isArray(val) && val.length === 0)) return "-";

    if (Array.isArray(val)) {
      return val
        .map((item) => {
          if (typeof item === "string") {
            const found = brandsList.find(
              (b) => b._id === item || b.id === item,
            );
            return found ? found.title : item;
          }
          if (item?.title) return item.title;
          if (item?._id) {
            const found = brandsList.find(
              (b) => b._id === item._id || b.id === item._id,
            );
            return found ? found.title : item._id;
          }
          return "-";
        })
        .join(", ");
    }

    if (typeof val === "string") {
      const found = brandsList.find((b) => b._id === val || b.id === val);
      return found ? found.title : val;
    }

    if (typeof val === "object") {
      if (val.title) return val.title;
      if (val._id) {
        const found = brandsList.find(
          (b) => b._id === val._id || b.id === val._id,
        );
        return found ? found.title : val._id;
      }
    }

    return "-";
  };

  const columns = [
    {
      key: "gallery",
      label: "Image",
      render: (_, row) => {
        const src = getImageSrc(row);
        if (!src) return "-";
        return (
          <FallbackImage
            src={src}
            alt={row.title || row.name || "Application image"}
            style={{
              width: 48,
              height: 48,
              borderRadius: "8px",
              objectFit: "cover",
              background: "#f3f0f7",
            }}
          />
        );
      },
    },
    {
      key: "title",
      label: "Title",
      render: (_, row) => row.title || row.name || "-",
    },

    {
      key: "industry",
      label: "Industry",
      render: (value) => getIndustryTitle(value),
    },
    {
      key: "brand",
      label: "Brand",
      render: (value) => getBrandTitle(value),
    },
    {
      key: "status",
      label: "Status",
      render: (_, row) => {
        const status = getApplicationImageStatus(row);
        const statusClass = getApplicationImageStatusClass(status);
        const rawStatus =
          row?.status ??
          row?.approvalStatus ??
          row?.approval_status ??
          row?.imageStatus;
        const label = rawStatus
          ? String(rawStatus)
          : status.charAt(0).toUpperCase() + status.slice(1);

        return <span className={`status-badge ${statusClass}`}>{label}</span>;
      },
    },
    {
      key: "actions",
      label: "Actions",
      render: (_, row) => (
        <ActionMenu
          row={row}
          onView={handleView}
          onEdit={handleEdit}
          onPending={
            onPending && getApplicationImageStatus(row) !== "pending"
              ? onPending
              : undefined
          }
          onApprove={
            onApprove &&
            canApprove &&
            getApplicationImageStatus(row) !== "approved"
              ? onApprove
              : undefined
          }
          editLabel="Edit Status"
        />
      ),
    },
  ];

  return <Datatable data={data} columns={columns} />;
};

export default ApplicationImagesData;
